import { useMemo, useState } from "react";
import { Button, Card, FormField, Input, Badge, useToast } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { ForbiddenInline } from "./ForbiddenInline";
import { Mono } from "./Mono";
import { useBillDemande, usePayDemande } from "../lib/api";
import { CAPS } from "../lib/caps";
import { formatMontant, parseMontantToCentimes } from "../lib/format";
import type { Demande } from "../lib/types";

/**
 * BillingPanel — the reception → finance handoff. Step 1: issue the facture (reception.demande.update
 * server-side maps to billDemande). Step 2: collect cash behind the open-caisse gate + the
 * finance.paiement.collect capability; the gate returns 409 when no caisse is open — we surface that
 * as actionable guidance, not a raw error. Monnaie-à-rendre is computed client-side for the cashier.
 */
export function BillingPanel({ demande }: { demande: Demande }) {
  const { t } = useT();
  const toast = useToast();
  const canIssue = useCan(CAPS.demandeUpdate);
  const canCollect = useCan(CAPS.paiementCollect);

  const bill = useBillDemande(demande.id);
  const pay = usePayDemande(demande.id);

  const due = useMemo(() => {
    if (typeof demande.total_ttc === "number") return demande.total_ttc;
    return demande.lignes
      .filter((l) => l.statut !== "annulee")
      .reduce((s, l) => s + l.prix_applique - (l.remise_appliquee ?? 0), 0);
  }, [demande]);

  const [tenduInput, setTenduInput] = useState("");
  const tenduCentimes = parseMontantToCentimes(tenduInput);
  const rendu = tenduCentimes != null ? tenduCentimes - due : null;

  const issued = !!demande.facture_id || !!bill.data;

  const issue = () => {
    bill.mutate(undefined, {
      onSuccess: () => toast.success(t("reception.billing.issued")),
      onError: (e) => toast.error(e.message || t("reception.error.generic")),
    });
  };

  const collect = () => {
    if (tenduCentimes == null || tenduCentimes < due) return;
    pay.mutate(
      { montant: due, montant_tendu: tenduCentimes },
      {
        onSuccess: () => {
          toast.success(t("reception.billing.paid"));
          setTenduInput("");
        },
        onError: (e) => {
          // requireOpenCaisse → 409; surface the actionable caisse hint.
          if (e.httpStatus === 409) toast.error(t("reception.billing.caisseClosed"));
          else toast.error(e.message || t("reception.billing.payError"));
        },
      },
    );
  };

  const paid = !!pay.data;

  if (!canIssue && !canCollect) {
    return <ForbiddenInline requiredCapabilities={[CAPS.demandeUpdate, CAPS.paiementCollect]} />;
  }

  return (
    <Card title={t("reception.billing.title")}>
      <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: `${token.s2} ${token.s5}`,
            margin: 0,
          }}
        >
          <dt style={{ color: token.slate }}>{t("reception.billing.montant")}</dt>
          <dd style={{ margin: 0 }}>
            <Mono>{formatMontant(due)}</Mono>
          </dd>
          {(demande.total_remise ?? 0) > 0 && (
            <>
              <dt style={{ color: token.slate }}>{t("reception.billing.remise")}</dt>
              <dd style={{ margin: 0 }}>
                <Mono>{formatMontant(demande.total_remise)}</Mono>
              </dd>
            </>
          )}
          <dt style={{ color: token.slate }}>{t("reception.billing.facture")}</dt>
          <dd style={{ margin: 0 }}>
            {issued ? (
              <Badge tone="success">{t("reception.billing.issued")}</Badge>
            ) : (
              <Badge tone="warn">{t("reception.billing.needIssue")}</Badge>
            )}
          </dd>
        </dl>

        {!issued && canIssue && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={issue} loading={bill.isPending}>
              {t("reception.billing.issue")}
            </Button>
          </div>
        )}

        {issued && canCollect && !paid && (
          <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
            <FormField label={t("reception.billing.montantTendu")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  inputMode="decimal"
                  placeholder="0,00"
                  value={tenduInput}
                  onChange={(e) => setTenduInput(e.target.value)}
                  style={{ maxWidth: "18ch" }}
                />
              )}
            </FormField>
            {rendu != null && (
              <div style={{ color: rendu < 0 ? token.danger : token.slate }}>
                {t("reception.billing.rendu")}: <Mono>{formatMontant(Math.max(0, rendu))}</Mono>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={collect}
                loading={pay.isPending}
                disabled={tenduCentimes == null || tenduCentimes < due}
              >
                {t("reception.billing.pay")}
              </Button>
            </div>
          </div>
        )}

        {paid && (
          <Badge tone="success">
            {t("reception.billing.paid")}
            {pay.data?.paiement?.monnaie_rendue != null && (
              <>
                {" · "}
                <Mono>{formatMontant(pay.data.paiement.monnaie_rendue)}</Mono>
              </>
            )}
          </Badge>
        )}
      </div>
    </Card>
  );
}
